/* eslint-disable max-statements */

import { Alert, Button, Group, LoadingOverlay, Stack, Switch, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { NewBatchProps } from '@override';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import { BatchTypeEnum, TemplateType } from '@e-origin/shared';

import { Select, TextInput, UploadFile } from '@components';
import { useBulkUploadBatches, useCreateBatch, useUpdateBatch } from '@hooks/batch';
import { useCustomersNamesAndIds } from '@hooks/customer';
import { useDeclarant } from '@hooks/declarant';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useTemplates } from '@hooks/useTemplates';

const newBatchSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: 'Required' })
      .regex(/^[a-zA-Z0-9-]*$/g, { message: 'Name is invalid' }),
    customer: z.string().min(1, { message: 'Required' }),
    template: z.string().min(1, { message: 'Required' }),
    file: z.instanceof(File).optional().nullable(),
    files: z.array(z.instanceof(File)).optional(),
    hvSplittingCustomer: z.string().optional(),
    hvSplittingTemplate: z.string().optional(),
    h7ExcludedCustomer: z.string().optional(),
    h7ExcludedTemplate: z.string().optional(),
    isBulkUpload: z.boolean().optional(),
  })
  .refine((data) => !data.isBulkUpload || (data.files && data.files.length > 0), {
    message: 'At least one file is required for bulk upload',
    path: ['files'],
  })
  .refine((data) => data.isBulkUpload || data.file instanceof File, {
    message: 'A file is required for single upload',
    path: ['file'],
  });

type NewBatchValues = z.infer<typeof newBatchSchema>;

export const NewBatch: React.FC<NewBatchProps> = (props) => {
  const { afterCreate, isUpdate, onHide, updatedBatch, batchType } = props;

  const createBatchMutation = useCreateBatch();
  const bulkUploadBatchesMutation = useBulkUploadBatches();
  const updateBatchMutation = useUpdateBatch();

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [useDefaultHVSplitting, setUseDefaultHVSplitting] = useState(true);
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const { data: templatesList, isLoading } = useTemplates();
  const customersNamesAndIds = useCustomersNamesAndIds();
  const { data: declarant } = useDeclarant();

  const isCustomersNamesAndIdsEmpty = customersNamesAndIds.length === 0;

  const filteredTemplates = useMemo(() => {
    return templatesList
      ?.filter(
        (t) =>
          ((batchType === BatchTypeEnum.HIGH_VALUES && t.type === TemplateType.HIGH_VALUE_H1 && !t.isExport) ||
            (batchType === BatchTypeEnum.HIGH_VALUES_EXPORT && t.isExport) ||
            (batchType === BatchTypeEnum.LOW_VALUES && t.type === TemplateType.LOW_VALUE_H7)) &&
          t.published !== false,
      )
      .map((template) => ({
        label: template.name,
        value: template._id,
        versioningId: template.versioningId,
        published: template.published,
      }));
  }, [templatesList]);

  const getTemplateValue = useMemo(() => {
    if (!updatedBatch) return;

    return updatedBatch?.virtuals?.templatePublished
      ? filteredTemplates?.find((template) => template.value === updatedBatch.template)
      : filteredTemplates?.find((template) => template.versioningId === updatedBatch.virtuals?.templateVersioningId && template.published === true);
  }, [templatesList, updatedBatch]);

  const getDefaultCustomer = useMemo(() => {
    return batchType === BatchTypeEnum.LOW_VALUES
      ? declarant?.defaultCustomersAndTemplates?.lowValues?.b2cLv?.customer || ''
      : declarant?.defaultCustomersAndTemplates?.[batchType === BatchTypeEnum.HIGH_VALUES ? 'highValues' : 'highValuesExport']?.customer || '';
  }, [declarant, updatedBatch, batchType]);

  const getDefaultTemplate = useMemo(() => {
    const defaultTemplateId =
      batchType === BatchTypeEnum.LOW_VALUES
        ? declarant?.defaultCustomersAndTemplates?.lowValues?.b2cLv?.template || ''
        : declarant?.defaultCustomersAndTemplates?.[batchType === BatchTypeEnum.HIGH_VALUES ? 'highValues' : 'highValuesExport']?.template || '';

    return filteredTemplates?.some((t) => t.value === defaultTemplateId) ? defaultTemplateId : '';
  }, [declarant, updatedBatch, batchType, getTemplateValue, filteredTemplates]);

  const getDefaultHVSplittingCustomer = useMemo(() => {
    return batchType === BatchTypeEnum.LOW_VALUES ? declarant?.defaultCustomersAndTemplates?.lowValues?.b2cHv?.customer || '' : '';
  }, [declarant, batchType]);

  const filteredHVTemplates = useMemo(() => {
    return templatesList
      ?.filter((t) => t.type === TemplateType.HIGH_VALUE_H1 && !t.isExport && t.published !== false)
      .map((template) => ({
        label: template.name,
        value: template._id,
        versioningId: template.versioningId,
        published: template.published,
      }));
  }, [templatesList]);

  const getDefaultHVSplittingTemplate = useMemo(() => {
    if (batchType !== BatchTypeEnum.LOW_VALUES) return '';

    const defaultTemplateId = declarant?.defaultCustomersAndTemplates?.lowValues?.b2cHv?.template || '';
    return filteredHVTemplates?.some((t) => t.value === defaultTemplateId) ? defaultTemplateId : '';
  }, [declarant, batchType, filteredHVTemplates]);

  const getDefaultH7ExcludedCustomer = useMemo(() => {
    return batchType === BatchTypeEnum.LOW_VALUES ? declarant?.defaultCustomersAndTemplates?.lowValues?.b2cH7Excluded?.customer || '' : '';
  }, [declarant, batchType]);

  const getDefaultH7ExcludedTemplate = useMemo(() => {
    if (batchType !== BatchTypeEnum.LOW_VALUES) return '';

    const defaultTemplateId = declarant?.defaultCustomersAndTemplates?.lowValues?.b2cH7Excluded?.template || '';
    return filteredHVTemplates?.some((t) => t.value === defaultTemplateId) ? defaultTemplateId : '';
  }, [declarant, batchType, filteredHVTemplates]);

  const form = useFormWithNameInputProp<NewBatchValues>({
    initialValues: {
      name: updatedBatch?.name || '',
      customer: updatedBatch?.customer || getDefaultCustomer,
      template: getTemplateValue?.value || getDefaultTemplate,
      file: null as unknown as File,
      files: [] as unknown as File[],
      hvSplittingCustomer: getDefaultHVSplittingCustomer,
      hvSplittingTemplate: getDefaultHVSplittingTemplate,
      h7ExcludedCustomer: getDefaultH7ExcludedCustomer,
      h7ExcludedTemplate: getDefaultH7ExcludedTemplate,
      isBulkUpload: false,
    },
    validate: zodResolver(newBatchSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: NewBatchValues) => {
    setErrorMessage(null);
    setSubmitting(true);
    let isSuccess = false;

    const batchData = {
      ...values,
      ...(batchType === BatchTypeEnum.LOW_VALUES && !isUpdate && declarant?.functionalFeatures?.autoB2CSplitLVHV
        ? {
            hvSplittingCustomer: values.hvSplittingCustomer,
            hvSplittingTemplate: values.hvSplittingTemplate,
            h7ExcludedCustomer: values.h7ExcludedCustomer,
            h7ExcludedTemplate: values.h7ExcludedTemplate,
          }
        : {}),
    };

    if (isUpdate) {
      await updateBatchMutation.mutateAsync({ batch: batchData, batchId: updatedBatch?._id });
      isSuccess = true;
    } else {
      try {
        if (isBulkUpload) {
          await bulkUploadBatchesMutation.mutateAsync({ batch: batchData });
        } else {
          await createBatchMutation.mutateAsync({ batch: batchData });
        }

        isSuccess = true;
      } catch (error) {
        setErrorMessage((error as any)?.message ?? 'An error occurred while creating the batch');
      }
    }
    if (isSuccess) {
      afterCreate();
      onHide();
    }
    setSubmitting(false);
  };

  if (!templatesList) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  const handleBulkUploadSwitch = () => {
    const newValue = !isBulkUpload;
    setIsBulkUpload(newValue);
    form.setFieldValue('isBulkUpload', newValue);
    form.setFieldValue('files', []);
    form.setFieldValue('file', null);
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)} data-pw="new-batch-form">
      {isUpdate && batchType !== BatchTypeEnum.LOW_VALUES && (
        <Alert icon={<IconAlertTriangle size={16} />} mb="md">
          Update batch will only update the goods items. It has no effect on the header information (eg. stakeholders, documents, …).
        </Alert>
      )}
      {!isUpdate && <Switch label="Bulk Upload" checked={isBulkUpload} onChange={() => handleBulkUploadSwitch()} mb="md" />}
      {isBulkUpload && (
        <Alert icon={<IconAlertTriangle size={16} />} mb="md">
          Bulk Upload will use the unique reference as the batch name, and files are sorted alphabetically by filename and sorting numbers in numeric order to
          generate the index number for the name.
        </Alert>
      )}
      <Stack gap="sm" mb="xl">
        <TextInput
          {...form.getInputProps('name')}
          label={isBulkUpload ? 'Unique Reference' : 'Batch name'}
          disabled={isUpdate}
          data-pw="batch-name-input"
          size="lg"
        />
        {!isCustomersNamesAndIdsEmpty && (
          <Select
            {...form.getInputProps('customer')}
            label="Select customer"
            data={customersNamesAndIds}
            disabled={isUpdate}
            searchable
            size="lg"
            data-pw="select-customer-dropdown"
          />
        )}
        <Select
          {...form.getInputProps('template')}
          label="Select template"
          data={filteredTemplates}
          disabled={isUpdate}
          searchable
          size="lg"
          data-pw="select-template-dropdown"
        />
        {batchType === BatchTypeEnum.LOW_VALUES && !isUpdate && declarant.functionalFeatures?.autoB2CSplitLVHV && (
          <>
            <Switch
              label="Use default B2C HV splitting template and customer"
              checked={useDefaultHVSplitting}
              onChange={(event) => setUseDefaultHVSplitting(event.currentTarget.checked)}
            />

            {!useDefaultHVSplitting && (
              <>
                <Text size="sm" fw={500} mt="md">
                  B2C HV Splitting &gt; 150€
                </Text>
                {!isCustomersNamesAndIdsEmpty && (
                  <Select {...form.getInputProps('hvSplittingCustomer')} label="Select HV customer" data={customersNamesAndIds} searchable size="lg" />
                )}
                <Select {...form.getInputProps('hvSplittingTemplate')} label="Select HV template" data={filteredHVTemplates} searchable size="lg" />

                <Text size="sm" fw={500} mt="md">
                  B2C HV Splitting H7 excluded HS codes
                </Text>
                {!isCustomersNamesAndIdsEmpty && (
                  <Select {...form.getInputProps('h7ExcludedCustomer')} label="Select HV customer" data={customersNamesAndIds} searchable size="lg" />
                )}
                <Select {...form.getInputProps('h7ExcludedTemplate')} label="Select HV template" data={filteredHVTemplates} searchable size="lg" />
              </>
            )}
          </>
        )}
        {isBulkUpload ? (
          <UploadFile {...form.getInputProps('files')} isMulti type="xlsx" p="xs" />
        ) : (
          <UploadFile {...form.getInputProps('file')} isMulti={false} type="xlsx" p="xs" />
        )}
        {isCustomersNamesAndIdsEmpty && (
          <Text c="#FF0000" fw={'bold'}>
            You don't have any customers. Please, create a customer.
          </Text>
        )}
        {errorMessage && (
          <Text c="#FF0000" fw={'bold'}>
            {errorMessage}
          </Text>
        )}
      </Stack>
      <Group justify="center">
        <Button type="button" variant="transparent" onClick={onHide} disabled={submitting} size="lg">
          Cancel
        </Button>
        <Button type="submit" data-pw="submit-btn" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg">
          {isUpdate ? 'Update' : 'Process'}
        </Button>
      </Group>
    </form>
  );
};
